import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { PedidoHistoricoDTO } from '../../models/cardapio-digital/cardapio-digital.model';

// Estados considerados finais — tracking nao deve ser restaurado apos atingi-los
const ESTADOS_FINAIS_HISTORICO = ['FINALIZADO', 'CANCELADO'];

/**
 * Servico de gerenciamento do historico de pedidos do cardapio digital.
 * Usa localStorage para persistencia, com BehaviorSubject para reatividade.
 * Segue o mesmo padrao do CardapioPedidoService.
 */
@Injectable({ providedIn: 'root' })
export class CardapioHistoricoService {
  private readonly CACHE_PREFIX = 'cardapio_historico_';
  private readonly TRACKING_PENDENTE_PREFIX = 'cardapio_tracking_pendente_';
  private readonly MAX_PEDIDOS = 50;
  private readonly historico$ = new BehaviorSubject<PedidoHistoricoDTO[]>([]);

  historicoObservable = this.historico$.asObservable();

  get pedidos(): PedidoHistoricoDTO[] { return this.historico$.value; }
  get temHistorico(): boolean { return this.historico$.value.length > 0; }

  /** Carrega historico do localStorage para o slug informado */
  carregarPorSlug(slug: string): void {
    const pedidos = this.lerDoCache(slug);
    this.historico$.next(pedidos);
  }

  /** Adiciona pedido no inicio da lista, aplica limite FIFO e persiste */
  adicionarPedido(slug: string, pedido: PedidoHistoricoDTO): void {
    const lista = this.lerDoCache(slug);
    lista.unshift(pedido); // Adiciona no inicio (mais recente primeiro)
    if (lista.length > this.MAX_PEDIDOS) {
      lista.splice(this.MAX_PEDIDOS); // Remove excedentes do final (mais antigos)
    }
    this.sincronizar(slug, lista);
    this.historico$.next(lista);
  }

  /** Remove todos os pedidos da empresa */
  limparHistorico(slug: string): void {
    try {
      localStorage.removeItem(this.CACHE_PREFIX + slug);
    } catch { /* localStorage indisponivel */ }
    this.historico$.next([]);
  }

  /**
   * Salva o idCurto do pedido em tracking ativo no localStorage.
   * Permite restaurar o tracking apos refresh da pagina.
   * Deve ser chamado imediatamente apos finalizar um pedido.
   */
  salvarTrackingPendente(slug: string, idCurto: string): void {
    try {
      localStorage.setItem(this.TRACKING_PENDENTE_PREFIX + slug, idCurto);
    } catch { /* localStorage indisponivel */ }
  }

  /**
   * Remove o tracking pendente do localStorage.
   * Deve ser chamado quando o usuario sai voluntariamente da tela de tracking
   * ou quando o pedido atinge um estado final.
   */
  limparTrackingPendente(slug: string): void {
    try {
      localStorage.removeItem(this.TRACKING_PENDENTE_PREFIX + slug);
    } catch { /* localStorage indisponivel */ }
  }

  /**
   * Retorna o pedido em tracking ativo para o slug informado.
   * Busca o idCurto salvo no localStorage e localiza o pedido correspondente no historico.
   * Retorna null se nao houver tracking pendente ou se o pedido ja estiver em estado final.
   *
   * O estadoDePedido do PedidoHistoricoDTO e sempre 'ENVIADO' (valor do campo `status`),
   * mas o campo real do estado do backend fica no codigoDePedido -- o polling ira consultar
   * o estado atual ao retomar. Por isso, apenas pedidos COM codigoDePedido sao restaurados.
   */
  recuperarTrackingPendente(slug: string): PedidoHistoricoDTO | null {
    try {
      const idCurto = localStorage.getItem(this.TRACKING_PENDENTE_PREFIX + slug);
      if (!idCurto) return null;

      const pedidos = this.lerDoCache(slug);
      const pedido = pedidos.find(p => p.idCurto === idCurto) || null;

      // Nao restaurar se o pedido nao existir no historico ou nao tiver codigoDePedido
      if (!pedido || !pedido.codigoDePedido) {
        this.limparTrackingPendente(slug);
        return null;
      }

      return pedido;
    } catch {
      return null;
    }
  }

  /**
   * Notifica que o estado do pedido foi atualizado pelo polling.
   * Quando o estado for final, remove o tracking pendente do localStorage
   * para evitar restaura-lo em um refresh posterior.
   * Nao altera o campo `status` do PedidoHistoricoDTO (que tem semântica propria).
   */
  atualizarEstadoPedido(slug: string, idCurto: string, estadoDePedido: string): void {
    // Atualizar o estado no historico persistido
    const pedidos = this.lerDoCache(slug);
    const pedido = pedidos.find(p => p.idCurto === idCurto);
    if (pedido) {
      pedido.estadoDePedido = estadoDePedido;
      this.sincronizar(slug, pedidos);
    }

    // Se o estado for final, remover tracking pendente automaticamente
    if (ESTADOS_FINAIS_HISTORICO.includes(estadoDePedido)) {
      this.limparTrackingPendente(slug);
    }
  }

  // --- Persistencia localStorage ---

  private sincronizar(slug: string, pedidos: PedidoHistoricoDTO[]): void {
    try {
      localStorage.setItem(this.CACHE_PREFIX + slug, JSON.stringify(pedidos));
    } catch {
      // localStorage indisponivel ou cheio
    }
  }

  private lerDoCache(slug: string): PedidoHistoricoDTO[] {
    try {
      const raw = localStorage.getItem(this.CACHE_PREFIX + slug);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed as PedidoHistoricoDTO[];
    } catch {
      return [];
    }
  }
}
